import { useEffect, useRef, useState } from "react";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import type {
  ArtworkFinding,
  ProofDecision,
} from "@shared-core/data/customer-link-adapter";
import { Card } from "../../app/components/primitives/card";
import { Button } from "../../app/components/primitives/button";
import { CheckCircle2, XCircle } from "lucide-react";

// The customer's decision on one proof, on the order page and on the
// per-proof page (doc 39 §7, doc 66 §6.5): approve, or ask for changes with a
// reason and words ("Does this look right?", "What needs changing?").
//
// Worth-knowing findings (doc 39 §6): Approve needs a tick against each one
// and sends their ids (`accepted_finding_ids`). A change request sends its
// reason's code and the customer's words separately. The buttons are
// outlined: the order page keeps its one filled button for paying (Ben, 5 Oct).

export function ProofDecisionForm({
  onSubmit,
  approveConsequence,
  findingsToAccept,
  reasonOptions,
  wordingVersion,
  idPrefix,
}: {
  /** Records the decision. Throw to show the error on the form. */
  onSubmit: (decision: ProofDecision) => Promise<void>;
  /** The one real consequence of approving, said up front. */
  approveConsequence: string;
  /** The worth-knowing findings, each needing a tick to approve. */
  findingsToAccept: ArtworkFinding[];
  /** What a change request chooses from (the read's, with `decide_url`). */
  reasonOptions: Array<{ code: string; label: string }>;
  /** The wording these sentences came from, sent back with the decision. */
  wordingVersion: number;
  /** Keeps ids unique when several forms could share a page. */
  idPrefix: string;
}) {
  const [requestingChanges, setRequestingChanges] = useState(false);
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set());
  const [ticksError, setTicksError] = useState<string | null>(null);
  const [reasonCode, setReasonCode] = useState("");
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [commentError, setCommentError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const firstTickRef = useRef<HTMLInputElement>(null);
  const firstReasonRef = useRef<HTMLInputElement>(null);
  const commentRef = useRef<HTMLTextAreaElement>(null);

  // Progressive disclosure moves focus into the revealed field.
  useEffect(() => {
    if (requestingChanges) firstReasonRef.current?.focus();
  }, [requestingChanges]);

  async function submit(decision: ProofDecision) {
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(decision);
    } catch (err) {
      setSubmitting(false);
      setError(
        mutationErrorMessage(
          err,
          "We couldn't record your response. Please try again.",
        ),
      );
    }
  }

  function approve() {
    if (findingsToAccept.some((f) => !ticked.has(f.id))) {
      setTicksError(
        findingsToAccept.length === 1
          ? "Tick the point above to approve this proof as it is, or ask for changes."
          : "Tick each point above to approve this proof as it is, or ask for changes.",
      );
      firstTickRef.current?.focus();
      return;
    }
    setTicksError(null);
    void submit({
      outcome: "approved",
      accepted_finding_ids: findingsToAccept.map((f) => f.id),
      wording_version: wordingVersion,
    });
  }

  // A reason must be picked and words given whichever reason: the code only
  // categorises; the words are what our studio acts on.
  function sendChanges() {
    if (reasonCode === "") {
      setReasonError("Choose what needs changing before you send this.");
      firstReasonRef.current?.focus();
      return;
    }
    setReasonError(null);
    if (comment.trim().length === 0) {
      setCommentError("Please add a few words so we know what to fix.");
      commentRef.current?.focus();
      return;
    }
    setCommentError(null);
    void submit({
      outcome: "rejected",
      reason_code: reasonCode,
      comment: comment.trim(),
      wording_version: wordingVersion,
    });
  }

  const ticksErrorId = `${idPrefix}-ticks-error`;
  const reasonErrorId = `${idPrefix}-reason-error`;
  const commentId = `${idPrefix}-comment`;
  const commentErrorId = `${idPrefix}-comment-error`;

  return (
    <Card className="space-y-4 p-5">
      <div className="space-y-1">
        <p className="text-base font-medium">Does this look right?</p>
        <p className="text-sm text-muted-foreground">{approveConsequence}</p>
      </div>

      {error && (
        <p role="alert" className="text-sm text-red-700 dark:text-red-400">
          {error}
        </p>
      )}

      {!requestingChanges ? (
        <div className="space-y-3">
          {findingsToAccept.length > 0 && (
            <fieldset
              className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/20"
              aria-invalid={ticksError ? true : undefined}
              aria-describedby={ticksError ? ticksErrorId : undefined}
            >
              <legend className="px-1 text-sm font-medium">
                Before you approve
              </legend>
              {findingsToAccept.map((finding, index) => (
                <label
                  key={finding.id}
                  className="flex items-start gap-2 text-sm"
                >
                  <input
                    ref={index === 0 ? firstTickRef : undefined}
                    type="checkbox"
                    className="mt-1"
                    checked={ticked.has(finding.id)}
                    onChange={(e) => {
                      const next = new Set(ticked);
                      if (e.target.checked) next.add(finding.id);
                      else next.delete(finding.id);
                      setTicked(next);
                      if (ticksError) setTicksError(null);
                    }}
                    disabled={submitting}
                  />
                  <span>
                    I&apos;ve seen this and want it printed as it is:{" "}
                    <span className="text-muted-foreground">
                      {finding.sentence}
                    </span>
                  </span>
                </label>
              ))}
              {ticksError && (
                <p
                  id={ticksErrorId}
                  role="alert"
                  className="text-sm text-red-700 dark:text-red-400"
                >
                  {ticksError}
                </p>
              )}
            </fieldset>
          )}
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={approve} disabled={submitting}>
              <CheckCircle2 className="size-4" />
              Yes, approve
            </Button>
            <Button
              variant="outline"
              onClick={() => setRequestingChanges(true)}
              disabled={submitting}
            >
              <XCircle className="size-4" />I need changes
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <fieldset
            className="space-y-2"
            aria-invalid={reasonError ? true : undefined}
            aria-describedby={reasonError ? reasonErrorId : undefined}
          >
            <legend className="text-sm font-medium">
              What needs changing?
            </legend>
            {reasonOptions.map((reason, index) => (
              <label
                key={reason.code}
                className="flex items-center gap-2 text-sm"
              >
                <input
                  ref={index === 0 ? firstReasonRef : undefined}
                  type="radio"
                  name={`${idPrefix}-change-reason`}
                  value={reason.code}
                  checked={reasonCode === reason.code}
                  onChange={() => {
                    setReasonCode(reason.code);
                    if (reasonError) setReasonError(null);
                  }}
                  disabled={submitting}
                />
                {reason.label}
              </label>
            ))}
            {reasonError && (
              <p
                id={reasonErrorId}
                role="alert"
                className="text-sm text-red-700 dark:text-red-400"
              >
                {reasonError}
              </p>
            )}
          </fieldset>
          <div className="space-y-2">
            <label htmlFor={commentId} className="text-sm font-medium">
              Tell us more
              <span className="text-muted-foreground"> (required)</span>
            </label>
            <textarea
              ref={commentRef}
              id={commentId}
              className="w-full rounded-md border border-input bg-background p-2 text-sm"
              rows={3}
              maxLength={2000}
              value={comment}
              onChange={(e) => {
                setComment(e.target.value);
                if (commentError) setCommentError(null);
              }}
              disabled={submitting}
              aria-invalid={commentError ? true : undefined}
              aria-describedby={commentError ? commentErrorId : undefined}
            />
            {commentError && (
              <p
                id={commentErrorId}
                role="alert"
                className="text-sm text-red-700 dark:text-red-400"
              >
                {commentError}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setRequestingChanges(false);
                setReasonError(null);
                setCommentError(null);
              }}
              disabled={submitting}
            >
              Back
            </Button>
            <Button
              variant="outline"
              onClick={sendChanges}
              disabled={submitting}
            >
              <XCircle className="size-4" />
              Send my comments
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
